/**
 * Is this `layout_json` a correct rendering of its section's questions?
 *
 * This is the definition of valid, and it lives here rather than in the Forms
 * Manager page because three callers need the same answer: the editor, the
 * generation loop, and the CLI the MCP shells out to. An agent that cannot run
 * this can only find out its layout is broken when a human relays the error.
 *
 * Deliberately free of React, the DOM, and the API client, so it runs
 * unchanged in a browser and in Node. Nothing reachable from here imports from
 * `pages/` — that is what lets the CLI run this outside a browser, so keep it
 * that way rather than reaching upward for a type.
 *
 * Separate from the renderer's own compile gate (`getLayoutCompileError`),
 * which answers a narrower question — *can this render at all* — during render
 * of a live visit.
 *
 * ## Adding a rule
 *
 * Write a {@link LayoutIssueDetector} and add it to {@link DETECTORS}. That is
 * the whole change: every surface — badges, detail panel, save confirmation,
 * repair prompt, CLI JSON, CLI text — renders whatever the list contains and
 * none of them names a kind.
 *
 * It did not use to be that way. Each surface enumerated the issue kinds by
 * hand, so one new rule meant a dozen edits across three files, and the ones
 * that got missed failed silently: the tab went red while the panel showed
 * nothing, and the repair assistant was never told about the rule it was being
 * asked to satisfy. Please do not reintroduce a per-kind branch downstream of
 * here — put what the surface needs on {@link LayoutIssue} instead.
 */

import { collectLayoutPartialRadioValuesByQid } from '../collectLayoutRadioOptionValuesByQid';
import {
  collectLayoutPlainCssHiddenQids,
  type LayoutPlainCssHiddenQid,
} from '../collectLayoutPlainCssHiddenQids';
import { collectLayoutReferencedQids } from '../collectLayoutReferencedQids';
import { collectLayoutRenderableFieldQids } from '../collectLayoutRenderableFieldQids';
import {
  collectLayoutUnknownProps,
  formatUnknownLayoutPropSummary,
} from '../collectLayoutUnknownProps';
import {
  collectLayoutSchemaGateIssues,
  formatLayoutSchemaGateIssueSummary,
} from '../collectLayoutSchemaGateIssues';
import { computeMissingCmsVisibleLabels } from '../letteredCmsVisibleLabelCoverage';
import {
  computeRadioOptionSchemaMismatches,
  type RadioOptionSchemaMismatch,
} from '../radioOptionSchemaCoverage';
import { tryParseLayoutJson } from '../tryParseLayoutJson';
import type { LayoutNode } from '../types';
import { collectSectionCoverageQids } from '../../verification/bespokeCoverageSearch';
import type { Question } from '../../../types';
import { truncateJoinedList } from '../../../../../utils/formatting';

export type LayoutValidationResult =
  { ok: true; layout: unknown[] } | { ok: false; error: string };

export type LayoutIssueKind =
  | 'stray-qids'
  | 'uncovered-qids'
  | 'unknown-props'
  | 'radio-mismatch'
  | 'css-hidden'
  | 'schema-gate'
  | 'cms-labels';

/**
 * One kind of problem with a layout, carrying everything any surface needs to
 * render it.
 *
 * Every consumer maps over a list of these. If a surface needs something this
 * does not carry, add a field here rather than a `switch` on {@link kind} —
 * a field is inherited by future rules, a `switch` has to be revisited by
 * each one.
 */
export interface LayoutIssue {
  kind: LayoutIssueKind;
  /** Short label for a chip or badge. */
  label: string;
  /** Full heading for a detail list, without the count. */
  title: string;
  /**
   * How loud to render it: `destructive` for a layout that misrepresents the
   * schema, `warning` for one that is merely incomplete.
   */
  tone: 'destructive' | 'warning';
  /**
   * How the items read: `inline` for bare QIDs, which are better as one
   * wrapped comma list than as dozens of bullets; `list` for per-item
   * sentences.
   */
  display: 'inline' | 'list';
  count: number;
  /** Per-item summaries, already formatted for display. Never truncated. */
  items: string[];
  /** The full message, including how to fix it. Truncates long item lists. */
  error: string;
  /**
   * Items as the repair assistant should see them, when that differs from
   * {@link items} (the CMS rule wants its ids in parentheses, because that is
   * the shape it is asking for back). Defaults to {@link items}.
   */
  promptItems?: string[];
  /** Appended to the findings heading in the repair prompt. */
  promptHint?: string;
}

/**
 * Everything the detectors need, computed once.
 *
 * A detector takes the whole context and ignores what it does not use, so
 * adding a rule that needs a new derived value means adding it here rather
 * than threading a new argument through every detector.
 */
interface LayoutIssueContext {
  layout: LayoutNode[];
  questions: Question[];
  /** Every QID in the section subtree, sorted. */
  schemaQids: string[];
  schemaQidSet: Set<string>;
  /** QIDs with a renderable answer control in the layout. */
  layoutFieldIds: Set<string>;
}

type LayoutIssueDetector = (ctx: LayoutIssueContext) => LayoutIssue | null;

export function formatLayoutPlainCssHiddenSummary(
  issue: LayoutPlainCssHiddenQid
): string {
  return `${issue.qid} (${issue.reason})`;
}

export function formatRadioOptionSchemaMismatchSummary(
  issue: RadioOptionSchemaMismatch
): string {
  const parts: string[] = [issue.qid];
  if (issue.missingValues.length > 0) {
    parts.push(`missing: ${issue.missingValues.join(', ')}`);
  }
  if (issue.extraValues.length > 0) {
    parts.push(`extra: ${issue.extraValues.join(', ')}`);
  }
  return parts.join(' — ');
}

/** `(+N more …)`, or nothing when the list fits. */
function overflowSuffix(total: number, shown: number, noun?: string): string {
  if (total <= shown) return '';
  return ` (+${total - shown} more${noun ? ` ${noun}` : ''})`;
}

function detectStrayQids({
  layout,
  schemaQidSet,
}: LayoutIssueContext): LayoutIssue | null {
  const strayQids = [...collectLayoutReferencedQids(layout)]
    .filter(qid => !schemaQidSet.has(qid))
    .sort((a, b) => a.localeCompare(b));
  if (strayQids.length === 0) return null;

  return {
    kind: 'stray-qids',
    label: 'Stray QIDs',
    title: 'QIDs present in Roger Layout Schema that do not belong',
    tone: 'destructive',
    display: 'inline',
    count: strayQids.length,
    items: strayQids,
    error: `QIDs present in Roger Layout Schema that do not belong (${strayQids.length}): ${truncateJoinedList(
      strayQids,
      5
    )}.`,
  };
}

function uncoveredQidsIssue(uncoveredQids: string[]): LayoutIssue | null {
  if (uncoveredQids.length === 0) return null;
  return {
    kind: 'uncovered-qids',
    label: 'Uncovered QIDs',
    title: 'Required QIDs missing from Roger Layout Schema',
    tone: 'warning',
    display: 'inline',
    count: uncoveredQids.length,
    items: uncoveredQids,
    error: `Required QIDs missing from Roger Layout Schema (${uncoveredQids.length}): ${truncateJoinedList(uncoveredQids, 12)}.`,
  };
}

function detectUncoveredQids({
  schemaQids,
  layoutFieldIds,
}: LayoutIssueContext): LayoutIssue | null {
  return uncoveredQidsIssue(
    schemaQids
      .filter(qid => !layoutFieldIds.has(qid))
      .sort((a, b) => a.localeCompare(b))
  );
}

function detectUnknownProps({
  layout,
}: LayoutIssueContext): LayoutIssue | null {
  const unknownProps = collectLayoutUnknownProps(layout);
  if (unknownProps.length === 0) return null;

  const items = unknownProps.map(formatUnknownLayoutPropSummary);
  return {
    kind: 'unknown-props',
    label: 'Unknown props',
    title: 'Props not defined by their node type',
    tone: 'destructive',
    display: 'list',
    count: unknownProps.length,
    items,
    error: `Props not defined by their node type (${unknownProps.length}): ${items.slice(0, 8).join('; ')}${overflowSuffix(unknownProps.length, 8)}. These are dropped at render time, so the styling they look like they apply is absent. Check the node's interface in layout/types.ts — a table labels its columns with colHeaderClassName and rowLabelClassName, not headerClassName/labelClassName.`,
  };
}

function detectRadioMismatches({
  layout,
  questions,
}: LayoutIssueContext): LayoutIssue | null {
  const radioMismatches = computeRadioOptionSchemaMismatches(
    questions,
    collectLayoutPartialRadioValuesByQid(layout, questions)
  );
  if (radioMismatches.length === 0) return null;

  const items = radioMismatches.map(formatRadioOptionSchemaMismatchSummary);
  return {
    kind: 'radio-mismatch',
    label: 'Radio options',
    title: 'Radio table codes vs schema values[]',
    tone: 'warning',
    display: 'list',
    count: radioMismatches.length,
    items,
    error: `Radio table codes vs schema values (${radioMismatches.length}): ${items.slice(0, 6).join('; ')}${overflowSuffix(radioMismatches.length, 6, 'QIDs')}.`,
  };
}

function detectCssHiddenQids({
  layout,
  schemaQids,
}: LayoutIssueContext): LayoutIssue | null {
  const cssHiddenQids = collectLayoutPlainCssHiddenQids(layout, schemaQids);
  if (cssHiddenQids.length === 0) return null;

  const items = cssHiddenQids.map(formatLayoutPlainCssHiddenSummary);
  return {
    kind: 'css-hidden',
    label: 'CSS hidden',
    title: 'Required QIDs hidden with CSS',
    tone: 'destructive',
    display: 'list',
    count: cssHiddenQids.length,
    items,
    error: `Required QIDs hidden with CSS (${cssHiddenQids.length}): ${items.slice(0, 6).join('; ')}${overflowSuffix(cssHiddenQids.length, 6, 'QIDs')}. Remove display:none, visibility:hidden, opacity:0, hidden, invisible, opacity-0, etc. from required fields and their wrappers.`,
  };
}

function detectSchemaGateIssues({
  layout,
  questions,
}: LayoutIssueContext): LayoutIssue | null {
  const schemaGateIssues = collectLayoutSchemaGateIssues(layout, questions);
  if (schemaGateIssues.length === 0) return null;

  const items = schemaGateIssues.map(formatLayoutSchemaGateIssueSummary);
  return {
    kind: 'schema-gate',
    label: 'Schema gating',
    title: 'Schema parent/child gating',
    tone: 'destructive',
    display: 'list',
    count: schemaGateIssues.length,
    items,
    error: `Schema parent/child gating (${schemaGateIssues.length}): ${items.slice(0, 6).join('; ')}${overflowSuffix(schemaGateIssues.length, 6, 'QIDs')}. Top-level schema questions must render outside conditional and disabled-when blocks; gated nested subitems may only reference schema ancestors with matching subitems answer keys. For disabled-when, whenAll means "keep enabled while" (same eq/includes as conditional), not the opposite answer.`,
  };
}

function detectMissingCmsVisibleLabels({
  layout,
  questions,
  layoutFieldIds,
}: LayoutIssueContext): LayoutIssue | null {
  const missingCmsVisibleLabels = computeMissingCmsVisibleLabels(
    questions,
    layout,
    layoutFieldIds
  );
  if (missingCmsVisibleLabels.length === 0) return null;

  const parenthesized = missingCmsVisibleLabels.map(id => `(${id})`);
  return {
    kind: 'cms-labels',
    label: 'CMS labels',
    title: 'Lettered CMS item ids missing visible labels',
    tone: 'destructive',
    display: 'inline',
    count: missingCmsVisibleLabels.length,
    items: missingCmsVisibleLabels,
    error: `Lettered CMS item ids missing visible layout labels (${missingCmsVisibleLabels.length}): ${truncateJoinedList(missingCmsVisibleLabels, 12)}${overflowSuffix(missingCmsVisibleLabels.length, 12)}. Add each base id once as user-visible text, preferably in parentheses e.g. (GG0100) in a static or section-label—not per-answer suffixes (GG0100A, GG0100B) on field labelOverrides.`,
    promptItems: parenthesized,
    promptHint: ' — add in parentheses, e.g. (GG0100)',
  };
}

/**
 * Every rule, in the order problems are reported.
 *
 * Order is load-bearing: {@link validateLayoutJsonForSection} reports the first
 * issue, so this decides which reason the section tab shows.
 */
const DETECTORS: readonly LayoutIssueDetector[] = [
  detectStrayQids,
  detectUncoveredQids,
  detectRadioMismatches,
  detectCssHiddenQids,
  detectSchemaGateIssues,
  // After the schema rules: only the first issue reaches the editor, and a
  // dropped styling prop must not mask a field that renders on the wrong answer.
  detectUnknownProps,
  detectMissingCmsVisibleLabels,
];

export type SectionLayoutCoverageIssues =
  | { parseOk: false; parseError: string; issues: LayoutIssue[] }
  | { parseOk: true; layout: unknown[]; issues: LayoutIssue[] };

function layoutTextFromInput(
  layoutInput: string | unknown | null | undefined
): string {
  if (typeof layoutInput === 'string') return layoutInput;
  if (layoutInput == null) return '[]';
  return JSON.stringify(layoutInput);
}

export function collectSectionLayoutCoverageIssues(
  layoutInput: string | unknown | null | undefined,
  questions: Question[]
): SectionLayoutCoverageIssues {
  // Pseudo wrappers are already stripped server-side, so the section schema
  // here contains only real, renderable questions.
  const bespokeQuestions = questions;
  const parsed = tryParseLayoutJson(layoutTextFromInput(layoutInput));
  const schemaQids = collectSectionCoverageQids(bespokeQuestions);

  // A layout that will not parse covers nothing, so every schema QID is
  // uncovered — the one finding that is still true without a layout to read.
  const nothingCovered = (parseError: string): SectionLayoutCoverageIssues => ({
    parseOk: false,
    parseError,
    issues: [
      uncoveredQidsIssue([...schemaQids].sort((a, b) => a.localeCompare(b))),
    ].filter((issue): issue is LayoutIssue => issue !== null),
  });

  if (!parsed.ok) return nothingCovered(parsed.error);

  const layout = parsed.layout;
  try {
    const ctx: LayoutIssueContext = {
      layout,
      questions: bespokeQuestions,
      schemaQids,
      schemaQidSet: new Set(schemaQids),
      layoutFieldIds: collectLayoutRenderableFieldQids(layout),
    };

    return {
      parseOk: true,
      layout,
      issues: DETECTORS.map(detect => detect(ctx)).filter(
        (issue): issue is LayoutIssue => issue !== null
      ),
    };
  } catch (err) {
    return nothingCovered(
      err instanceof Error ? err.message : 'Layout structure is invalid.'
    );
  }
}

export function sectionHasLayoutCoverageGaps(
  coverage: SectionLayoutCoverageIssues
): boolean {
  return !coverage.parseOk || coverage.issues.length > 0;
}

/** The issue of a given kind, for a surface that genuinely needs just one. */
export function findLayoutIssue(
  coverage: SectionLayoutCoverageIssues,
  kind: LayoutIssueKind
): LayoutIssue | undefined {
  return coverage.issues.find(issue => issue.kind === kind);
}

export function validateLayoutJsonForSection(
  raw: string,
  questions: Question[]
): LayoutValidationResult {
  const coverage = collectSectionLayoutCoverageIssues(raw, questions);
  if (!coverage.parseOk) {
    return { ok: false, error: coverage.parseError };
  }

  // First issue wins, so the tab's reason is the first thing the editor lists.
  const [first] = coverage.issues;
  if (first) return { ok: false, error: first.error };

  return { ok: true, layout: coverage.layout };
}
